// Parser konfiguračních kódů z QR. Vrací normalizované příkazy.
// Klíč: středník odděluje segmenty UVNITŘ příkazu (LOGIN:U:fe;P:fe;), takže nelze
// dělit na ':' globálně — klíčové slovo je před první ':', zbytek jsou ';'-segmenty.

export interface ParsedLogin {
    type: 'LOGIN'
    username: string
    password: string
}

export interface ParsedTicketsetsT {
    type: 'TICKETS_T'
    ids: number[]
}

export interface ParsedTicketsetsZ {
    type: 'TICKETS_Z'
    ids: number[]
}

export interface ParsedCounter {
    type: 'COUNTER'
    mode: 0 | 1
}

export type LanguageCode = 'cs' | 'en' | 'sk' | 'de' | 'pl' | 'hu' | 'hr'
export interface ParsedLanguage {
    type: 'LANGUAGE'
    lang: LanguageCode
}

export type ScanInfoMode = 'NONE' | 'PRICE' | 'TICKETSET' | 'TICKETSET-PRICE'
export interface ParsedScanInfo {
    type: 'SCAN_INFO'
    mode: ScanInfoMode
}

export type ParsedCommand =
    | ParsedLogin
    | ParsedTicketsetsT
    | ParsedTicketsetsZ
    | ParsedCounter
    | ParsedLanguage
    | ParsedScanInfo

export interface ParseResult {
    isConfig: boolean
    commands: ParsedCommand[]
    unknown: string[]
}

const KNOWN = ['LOGIN', 'TICKETS', 'COUNTER', 'LANGUAGE', 'SCAN-INFO']

interface Parsed {
    keyword: string
    segments: Record<string, string>
}

function parseSegments(part: string): Parsed | null {
    const trimmed = part.trim()
    if (!trimmed) return null

    const firstColon = trimmed.indexOf(':')
    const keyword = firstColon === -1 ? trimmed.replace(/;+\s*$/, '') : trimmed.slice(0, firstColon)
    const rest = firstColon === -1 ? '' : trimmed.slice(firstColon + 1)

    const segments: Record<string, string> = {}
    rest.split(';').forEach(seg => {
        const s = seg.trim()
        if (!s) return
        const i = s.indexOf(':')
        if (i === -1) return
        segments[s.slice(0, i)] = s.slice(i + 1)
    })

    return { keyword, segments }
}

function csvInts(value: string): number[] {
    return value
        .split(',')
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n))
}

// Deep-link QR: https://<host>/mobile.../?cmd=<urlencoded payload>. Vytáhni a
// dekóduj hodnotu cmd; holý payload (bez http+cmd=) vrať beze změny. Stejná
// logika jako čtečka (_unwrap_deeplink v screen_intro.py), aby kamera v appce
// uměla naskenovat ten samý deep-link QR jako foťák telefonu.
function unwrapDeepLink(raw: string): string {
    if (raw.startsWith('http') && raw.includes('cmd=')) {
        const after = raw.split('cmd=', 2)[1]
        const value = after.split('&', 1)[0]
        try {
            return decodeURIComponent(value)
        } catch {
            return value
        }
    }
    return raw
}

export function parseConfigCode(input: unknown): ParseResult {
    if (typeof input !== 'string') return { isConfig: false, commands: [], unknown: [] }

    const raw = unwrapDeepLink(input)
    const looksLikeConfig = KNOWN.some(k => raw.includes(k + ':'))
    if (!looksLikeConfig) return { isConfig: false, commands: [], unknown: [] }

    const commands: ParsedCommand[] = []
    const unknown: string[] = []

    raw.split('|').forEach(part => {
        const parsed = parseSegments(part)
        if (!parsed) return

        if (parsed.keyword === 'LOGIN') {
            commands.push({
                type: 'LOGIN',
                username: parsed.segments.U ?? '',
                password: parsed.segments.P ?? ''
            })
        } else if (parsed.keyword === 'TICKETS' && parsed.segments.T !== undefined) {
            commands.push({ type: 'TICKETS_T', ids: csvInts(parsed.segments.T) })
        } else if (parsed.keyword === 'TICKETS' && parsed.segments.Z !== undefined) {
            commands.push({ type: 'TICKETS_Z', ids: csvInts(parsed.segments.Z) })
        } else if (parsed.keyword === 'COUNTER' && parsed.segments.T !== undefined) {
            const raw = parsed.segments.T.trim()
            if (raw === '0') commands.push({ type: 'COUNTER', mode: 0 })
            else if (raw === '1') commands.push({ type: 'COUNTER', mode: 1 })
            else unknown.push(parsed.keyword)
        } else if (parsed.keyword === 'LANGUAGE' && parsed.segments.T !== undefined) {
            const raw = parsed.segments.T.trim()
            const langs: LanguageCode[] = ['cs', 'en', 'sk', 'de', 'pl', 'hu', 'hr']
            if ((langs as string[]).includes(raw)) {
                commands.push({ type: 'LANGUAGE', lang: raw as LanguageCode })
            } else {
                unknown.push(parsed.keyword)
            }
        } else if (parsed.keyword === 'SCAN-INFO' && parsed.segments.T !== undefined) {
            const raw = parsed.segments.T.trim()
            const modes: ScanInfoMode[] = ['NONE', 'PRICE', 'TICKETSET', 'TICKETSET-PRICE']
            if (raw && (modes as string[]).includes(raw)) {
                commands.push({ type: 'SCAN_INFO', mode: raw as ScanInfoMode })
            } else {
                unknown.push(parsed.keyword)
            }
        } else if (KNOWN.includes(parsed.keyword)) {
            // známé klíčové slovo bez rozpoznaného segmentu — ignoruj
        } else {
            unknown.push(parsed.keyword)
        }
    })

    return { isConfig: commands.length > 0 || unknown.length > 0, commands, unknown }
}
